// Reusable permission tree + grant cards layout for a single domain.
// Used by each permission tab (Registers, Controls, Actions, Events, Frameworks).
//
// Layout: 50/50 split — tree on left, grant cards on right.
// Tree nodes: bold + [R] or [R+W] suffix when grants exist.
// Left-click node → context menu (Add Grant / Delete Grant).
// Grant cards: sorted by path, show read/write, delete button.

import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { grantsApi, type Grant } from '@/lib/api/usersRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { Button } from '@/components/ui/button'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { useToast } from '@/lib/hooks/use-toast'
import { ChevronRight, ChevronDown, Trash2, ShieldCheck, ShieldPlus } from 'lucide-react'
import { cn } from '@/lib/utils'

// --- Types ---

/** Generic tree node shape — works for both registers and categories. */
export interface TreeNode {
  id: string
  parent_id: string | null
  path: string
  depth: number
  name: string
  has_children: boolean
  children: TreeNode[]
}

/** Props for the PermissionTab component. */
interface PermissionTabProps {
  roleId: string
  domain: Grant['domain']
  treeNodes: TreeNode[]
  isTreeLoading: boolean
  treeError: Error | null
  onRefetchTree: () => void
}

/** Build a lookup from node_id → grants for quick access. */
function buildGrantMap(grants: Grant[]): Map<string, Grant[]> {
  const map = new Map<string, Grant[]>()
  for (const g of grants) {
    const existing = map.get(g.node_id) ?? []
    existing.push(g)
    map.set(g.node_id, existing)
  }
  return map
}

/** Get permission suffix for a node: [R+W], [R], or empty. */
function permSuffix(nodeGrants: Grant[] | undefined): string {
  if (!nodeGrants || nodeGrants.length === 0) return ''
  const hasWrite = nodeGrants.some((g) => g.permission_type === 'write')
  const hasRead = nodeGrants.some((g) => g.permission_type === 'read')
  if (hasWrite && hasRead) return '[R+W]'
  if (hasWrite) return '[W]'
  if (hasRead) return '[R]'
  return ''
}

export function PermissionTab({
  roleId,
  domain,
  treeNodes,
  isTreeLoading,
  treeError,
  onRefetchTree,
}: PermissionTabProps) {
  const { slug } = useTenant()
  const api = useApi()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const grantsClient = grantsApi(api)

  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Fetch grants for this role, filtered by domain
  const {
    data: grants,
    error: grantsError,
    isLoading: grantsLoading,
    refetch: refetchGrants,
  } = useQuery({
    queryKey: queryKeys.grants.list(slug, { role_id: roleId, domain }),
    queryFn: () => grantsClient.list({ role_id: roleId, domain }),
  })

  const grantMap = useMemo(() => buildGrantMap(grants ?? []), [grants])

  // Sort grants by path for the cards panel
  const sortedGrants = useMemo(
    () => [...(grants ?? [])].sort((a, b) => a.node_path.localeCompare(b.node_path)),
    [grants],
  )

  const invalidateGrants = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.grants.list(slug, { role_id: roleId, domain }) })
    queryClient.invalidateQueries({ queryKey: queryKeys.roles.grants(slug, roleId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.roles.all(slug) })
  }, [queryClient, slug, roleId, domain])

  const createMutation = useMutation({
    mutationFn: (data: { node_id: string; permission_type: string }) =>
      grantsClient.create({
        role_id: roleId,
        domain,
        node_id: data.node_id,
        permission_type: data.permission_type,
      }),
    onSuccess: () => {
      invalidateGrants()
      toast({ title: 'Grant added' })
    },
    onError: (err) => {
      toast({
        title: 'Failed to add grant',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (grantId: string) => grantsClient.delete(grantId),
    onSuccess: () => {
      invalidateGrants()
      toast({ title: 'Grant removed' })
    },
    onError: (err) => {
      toast({
        title: 'Failed to remove grant',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  function toggleExpand(nodeId: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) next.delete(nodeId)
      else next.add(nodeId)
      return next
    })
  }

  function handleAddGrant(nodeId: string, permType: 'read' | 'write') {
    // If adding write, also add read if not present
    const nodeGrants = grantMap.get(nodeId) ?? []
    const hasRead = nodeGrants.some((g) => g.permission_type === 'read')

    if (permType === 'write' && !hasRead) {
      // Add read first, then write
      createMutation.mutate(
        { node_id: nodeId, permission_type: 'read' },
        {
          onSuccess: () => {
            createMutation.mutate({ node_id: nodeId, permission_type: 'write' })
          },
        },
      )
    } else {
      createMutation.mutate({ node_id: nodeId, permission_type: permType })
    }
  }

  // --- Error states ---
  if (treeError) {
    return <ErrorState message="Failed to load tree" technicalDetail={treeError.message} onRetry={onRefetchTree} />
  }
  if (grantsError) {
    return <ErrorState message="Failed to load grants" technicalDetail={grantsError.message} onRetry={() => refetchGrants()} />
  }

  // --- Loading ---
  const isLoading = isTreeLoading || grantsLoading

  return (
    <div className="flex h-[calc(100vh-18rem)] gap-0 overflow-hidden rounded-md border">
      {/* Left: Tree */}
      <div className="w-1/2 overflow-y-auto border-r p-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Nodes
        </div>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : treeNodes.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            No nodes configured for this domain.
          </div>
        ) : (
          <div>
            {treeNodes.map((node) => (
              <TreeNodeRow
                key={node.id}
                node={node}
                depth={0}
                expanded={expanded}
                grantMap={grantMap}
                onToggle={toggleExpand}
                onAddGrant={handleAddGrant}
                isCreating={createMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>

      {/* Right: Grant cards */}
      <div className="w-1/2 overflow-y-auto p-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Grants ({sortedGrants.length})
        </div>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : sortedGrants.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            No grants assigned. Right-click a node to add permissions.
          </div>
        ) : (
          <div className="space-y-2">
            {sortedGrants.map((grant) => (
              <GrantCard
                key={grant.id}
                grant={grant}
                onDelete={() => deleteMutation.mutate(grant.id)}
                isDeleting={deleteMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// --- Tree node row ---

interface TreeNodeRowProps {
  node: TreeNode
  depth: number
  expanded: Set<string>
  grantMap: Map<string, Grant[]>
  onToggle: (id: string) => void
  onAddGrant: (nodeId: string, permType: 'read' | 'write') => void
  isCreating: boolean
}

function TreeNodeRow({ node, depth, expanded, grantMap, onToggle, onAddGrant, isCreating }: TreeNodeRowProps) {
  const isExpanded = expanded.has(node.id)
  const hasChildren = node.children.length > 0
  const nodeGrants = grantMap.get(node.id)
  const suffix = permSuffix(nodeGrants)
  const hasGrants = !!suffix
  const hasRead = nodeGrants?.some((g) => g.permission_type === 'read') ?? false
  const hasWrite = nodeGrants?.some((g) => g.permission_type === 'write') ?? false

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={cn(
              'flex cursor-pointer items-center rounded px-2 py-1.5 text-sm hover:bg-muted/50',
            )}
            style={{ paddingLeft: `${depth * 20 + 8}px` }}
            onClick={() => hasChildren && onToggle(node.id)}
          >
            {/* Expand/collapse chevron */}
            <span className="mr-1 w-4 flex-shrink-0">
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )
              ) : null}
            </span>

            {/* Node name + permission suffix */}
            <span className={cn('truncate', hasGrants && 'font-semibold')}>
              {node.name}
            </span>
            {suffix && (
              <span className="ml-1.5 flex-shrink-0 text-xs font-medium text-blue-600">
                {suffix}
              </span>
            )}
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent>
          {!hasRead && (
            <ContextMenuItem
              onClick={() => onAddGrant(node.id, 'read')}
              disabled={isCreating}
            >
              <ShieldCheck className="mr-2 h-4 w-4" />
              Add Read
            </ContextMenuItem>
          )}
          {!hasWrite && (
            <ContextMenuItem
              onClick={() => onAddGrant(node.id, 'write')}
              disabled={isCreating}
            >
              <ShieldPlus className="mr-2 h-4 w-4" />
              Add Write {!hasRead && '(+ Read)'}
            </ContextMenuItem>
          )}
          {hasRead && hasWrite && (
            <ContextMenuItem disabled>
              All permissions granted
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {/* Children */}
      {isExpanded &&
        node.children.map((child) => (
          <TreeNodeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            grantMap={grantMap}
            onToggle={onToggle}
            onAddGrant={onAddGrant}
            isCreating={isCreating}
          />
        ))}
    </>
  )
}

// --- Grant card ---

interface GrantCardProps {
  grant: Grant
  onDelete: () => void
  isDeleting: boolean
}

function GrantCard({ grant, onDelete, isDeleting }: GrantCardProps) {
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{grant.node_name}</span>
          <span
            className={cn(
              'flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium',
              grant.permission_type === 'write'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-700',
            )}
          >
            {grant.permission_type === 'write' ? 'Write' : 'Read'}
          </span>
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {grant.node_path}
        </div>
      </div>
      {!grant.is_system_generated && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={isDeleting}
          title="Remove grant"
          className="ml-2 flex-shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
